import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import findIndex from 'lodash/findIndex';
import InnerHTML from '@Components/InnerHTML';
import { onResize } from '@Services/WindowHelper';
import { Button } from '@els/els-react--button';

import './Synonyms.scss';

function Synonyms({ list, prefix, showCount, separator, coefficient, isDotSeparated }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [more, setMore] = useState(0);
  const containerRef = useRef(null);
  const coeff = coefficient || 0.425;
  const onResizeUnsubscribe = useRef(null);

  const recalculateMore = useCallback(() => {
    const el = containerRef?.current;
    if (!el) return;
    const fontSize = parseFloat(window.getComputedStyle(el, null).getPropertyValue('font-size'));
    const widthInCh = el.offsetWidth / (fontSize * coeff);
    const maxAllowedLenForSynonyms = widthInCh * 2 - prefix.length;

    let tmpStr = '';
    const idx = findIndex(list, (synonym) => {
      tmpStr += synonym + separator;
      return tmpStr.length >= maxAllowedLenForSynonyms;
    });

    let newMore;
    if (idx === 0) {
      newMore = list.length - 1;
    } else if (idx !== -1) {
      newMore = list.length - idx;
    } else {
      newMore = 0;
    }
    setMore(newMore);
  }, [list, prefix, separator, coeff]);

  useEffect(() => {
    recalculateMore();
    const throttled = throttle(recalculateMore, 300);
    onResizeUnsubscribe.current = onResize(throttled);
    return () => {
      if (onResizeUnsubscribe.current) onResizeUnsubscribe.current();
    };
  }, [recalculateMore]);

  const getSynonymsHtmlStr = () => {
    const result = isExpanded ? list : list.slice(0, list.length - more);
    let htmlStr = `${prefix}${result.join(separator)}`;
    if (!isExpanded && more > 0) {
      htmlStr += separator;
    }
    return htmlStr;
  };

  const getLessMoreLabel = () =>
    more > 0 ? (
      <Button
        data-e2e="synonyms-toggle"
        type="link"
        size="small"
        className="u-els-margin-left-1o4"
        onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? '- show less' : ` +${showCount && more} more`}
      </Button>
    ) : null;
  const getSynonymsList = () => {
    const result = isExpanded ? list : list.slice(0, list.length - more);
    const resultArray = result.map((item, index) => (
      <React.Fragment key={item}>
        <InnerHTML tag="span" html={item} />
        {(index !== result.length - 1 || (!isExpanded && more > 0)) && <span className="separator" />}
      </React.Fragment>
    ));

    return (
      <div>
        <span>{prefix}</span>
        {resultArray}
        {getLessMoreLabel()}
      </div>
    );
  };

  return (
    <div className="rx-element-literature" ref={containerRef}>
      {!isDotSeparated && (
        <>
          <InnerHTML tag="span" html={getSynonymsHtmlStr()} />
          {getLessMoreLabel()}
        </>
      )}
      {isDotSeparated && getSynonymsList()}
    </div>
  );
}

Synonyms.propTypes = {
  list: PropTypes.array.isRequired,
  prefix: PropTypes.string.isRequired,
  showCount: PropTypes.bool.isRequired,
  separator: PropTypes.string,
  coefficient: PropTypes.number,
  isDotSeparated: PropTypes.bool,
};

export default Synonyms;
